import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

interface UserProfileData {
  id: number;
  username: string;
  display_name: string;
  avatar_url?: string;
  bio?: string;
  location?: string;
  website?: string;
  github_url?: string;
  twitter_url?: string;
  followers_count: number;
  following_count: number;
  public_prompts_count: number;
  created_at: string;
  updated_at?: string;
}

interface UserProfileProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ isOpen, onClose }) => {
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bio, setBio] = useState('');

  // 获取用户资料
  const fetchProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('请先登录');
        return;
      }

      const response = await fetch('/api/user/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setProfile(data.data);
        setBio(data.data.bio || '');
      } else {
        toast.error(data.message || '获取用户资料失败');
      }
    } catch (error) {
      console.error('获取用户资料失败:', error);
      toast.error('获取用户资料失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存用户资料
  const saveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('请先登录');
        return;
      }

      const response = await fetch('/api/user/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bio })
      });

      const data = await response.json();
      if (data.success) {
        setProfile(data.data);
        toast.success('个人简介更新成功');
        onClose();
      } else {
        if (data.errors && Array.isArray(data.errors)) {
          data.errors.forEach((error: string) => toast.error(error));
        } else {
          toast.error(data.message || '更新个人简介失败');
        }
      }
    } catch (error) {
      console.error('更新个人简介失败:', error);
      toast.error('更新个人简介失败');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchProfile();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden transition-colors duration-200"
      >
        {/* 头部 */}
        <div className="relative bg-gradient-to-r from-primary to-primary-light p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all duration-200"
          >
            <i className="fas fa-times text-white text-sm"></i>
          </button>
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-1">编辑个人资料</h2>
            <p className="text-white text-opacity-90 text-sm">完善您的个人信息</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">加载中...</p>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* 问候语 */}
            <div className="text-center bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                你好，{profile?.username}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                填写您的个人信息，让更多人认识你吧！
              </p>
            </div>

            {/* 个人简介编辑 */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200">
                <i className="fas fa-edit text-primary mr-2"></i>
                个人简介
              </label>
              <div className="relative">
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="介绍一下自己..."
                  maxLength={300}
                  rows={5}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 resize-none text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700"
                />
                <div className="absolute bottom-3 right-3 text-xs text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 px-2 py-1 rounded-md">
                  {bio.length}/300
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-3 p-6 bg-gray-50 dark:bg-gray-700 border-t border-gray-100 dark:border-gray-600">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500 rounded-xl transition-all duration-200 font-medium"
          >
            取消
          </button>
          <button
            onClick={saveProfile}
            disabled={saving}
            className="px-6 py-2.5 bg-gradient-to-r from-primary to-primary-light text-white rounded-xl hover:shadow-lg hover:shadow-primary/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
          >
            {saving && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default UserProfile; 